<template>
	<transition name="fade">
		<div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" @click.self="$emit('close')">
			<div class="flex sm:items-center sm:justify-center min-h-screen sm:px-4 sm:pt-4 sm:pb-20 text-center">
				<div class="fixed inset-0 transition-opacity bg-black bg-opacity-50 dark:bg-opacity-50" @click="$emit('close')"></div>
				<div class="inline-block align-bottom sm:align-middle bg-white dark:bg-gray-800 sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all min-h-screen w-full fixed inset-x-0 top-0 sm:static sm:min-h-0 sm:max-w-4xl sm:w-full">
					<div class="bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 px-4 py-4 sm:px-6">
						<h3 class="text-lg leading-6 font-medium text-white">
							{{ mode === 'add' ? 'Add Material' : 'Edit Material' }}
						</h3>
					</div>
					<div class="bg-white dark:bg-gray-800 px-3 pt-4 pb-3 sm:p-6 sm:pb-4 overflow-y-auto max-h-[calc(100vh-12rem)]">
						<div class="space-y-6">
							<div v-if="displayError" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
								<div class="flex items-start gap-3">
									<div class="flex-shrink-0">
										<svg class="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
											<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
										</svg>
									</div>
									<div>
										<p class="text-sm font-medium text-red-800 dark:text-red-200">{{ displayError }}</p>
									</div>
								</div>
							</div>

							<div class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
								<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">Basic Information</h4>
								<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
									<div class="md:col-span-2">
										<label class="label">File Name (Title) <span class="text-danger">*</span></label>
										<input v-model="localForm.title" type="text" class="input" placeholder="Enter title" />
									</div>

									<div class="md:col-span-2">
										<label class="label">Description</label>
										<textarea v-model="localForm.description" class="input" placeholder="Enter description" rows="3" />
									</div>

									<div>
										<label class="label">Type <span class="text-danger">*</span></label>
										<select v-model="localForm.type" class="input">
											<option value="Online form">Online form</option>
											<option value="URL Link">URL Link</option>
											<option value="Doc File">Doc File</option>
										</select>
									</div>

									<div>
										<label class="label">Status</label>
										<select v-model="localForm.status" class="input">
											<option value="active">Active</option>
											<option value="inactive">Inactive</option>
										</select>
									</div>
								</div>
							</div>

							<div class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
								<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">Viewers & Access</h4>
								<div class="space-y-5">
									<div>
										<label class="label">Viewers (By Position)</label>
										<div class="relative" ref="positionContainer">
											<input v-model="positionSearch" type="text" placeholder="Search positions..." @focus="positionOpen = true" class="input text-sm" />
											<div v-if="positionOpen" class="absolute z-20 mt-1 w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg max-h-48 overflow-y-auto overscroll-contain">
												<label v-for="pos in filteredPositions" :key="pos.id" class="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm">
													<input type="checkbox" :value="pos.id" v-model="selectedPositionIds" class="rounded border-gray-300 text-primary focus:ring-primary" />
													<span class="text-gray-700 dark:text-gray-300">{{ pos.name }}</span>
												</label>
												<p v-if="!positions.length" class="px-3 py-2 text-sm text-gray-400 italic">Loading positions...</p>
												<p v-else-if="!filteredPositions.length" class="px-3 py-2 text-sm text-gray-400 italic">No positions match</p>
											</div>
										</div>
										<div v-if="fetching" class="flex items-center gap-2 mt-2 text-sm text-slate-400">
											<Loader2 class="w-4 h-4 animate-spin" />
											Loading...
										</div>
										<div v-else-if="selectedPositionIds.length" class="flex flex-wrap gap-1.5 mt-2">
											<span v-for="id in selectedPositionIds" :key="'psel-' + id" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 dark:bg-primary-dark/10 text-primary dark:text-primary-dark">
												{{ getPositionName(id) }}
												<button type="button" @click="removePosition(id)" class="hover:text-primary/70 dark:hover:text-primary-dark/70 leading-none">
													<X class="w-3.5 h-3.5" />
												</button>
											</span>
										</div>
										<p v-else class="mt-1 text-xs text-gray-400 italic">None selected</p>
									</div>

									<div>
										<label class="label">Viewers (By Specific Employee)</label>
										<div class="relative" ref="employeeContainer">
											<input v-model="employeeSearch" type="text" placeholder="Search employees..." @focus="employeeOpen = true" class="input text-sm" />
											<div v-if="employeeOpen" class="absolute z-20 mt-1 w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg max-h-48 overflow-y-auto overscroll-contain">
												<label v-for="employee in filteredEmployees" :key="employee.id" class="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm">
													<input type="checkbox" :value="employee.id" v-model="selectedEmployeeIds" class="rounded border-gray-300 text-primary focus:ring-primary" />
													<span class="text-gray-700 dark:text-gray-300">{{ getEmployeeDisplayName(employee) }}</span>
												</label>
												<p v-if="!employees.length" class="px-3 py-2 text-sm text-gray-400 italic">Loading employees...</p>
												<p v-else-if="!filteredEmployees.length" class="px-3 py-2 text-sm text-gray-400 italic">No employees found</p>
												<p v-else-if="employeeListHint" class="px-3 py-2 text-xs text-gray-400 border-t border-gray-100 dark:border-gray-600">{{ employeeListHint }}</p>
											</div>
										</div>
										<div v-if="fetching" class="flex items-center gap-2 mt-2 text-sm text-slate-400">
											<Loader2 class="w-4 h-4 animate-spin" />
											Loading...
										</div>
										<div v-else-if="selectedEmployeeIds.length" class="flex flex-wrap gap-1.5 mt-2">
											<span v-for="id in selectedEmployeeIds" :key="'esel-' + id" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 dark:bg-primary-dark/10 text-primary dark:text-primary-dark">
												{{ getEmployeeName(id) }}
												<button type="button" @click="removeEmployee(id)" class="hover:text-primary/70 dark:hover:text-primary-dark/70 leading-none">
													<X class="w-3.5 h-3.5" />
												</button>
											</span>
										</div>
										<p v-else class="mt-1 text-xs text-gray-400 italic">None selected</p>
									</div>
								</div>
							</div>

							<div v-if="localForm.type === 'Doc File'" class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
								<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">File Upload</h4>
								<div>
									<label class="label">File <span v-if="mode === 'add'" class="text-danger">*</span></label>

									<div class="rounded-2xl border-2 border-dashed p-6 cursor-pointer select-none bg-white/60 dark:bg-gray-800/50 border-gray-300/70 dark:border-gray-600/60 hover:bg-white/80 dark:hover:bg-gray-800/70" :class="isDragOver ? 'ring-2 ring-primary/50 border-primary/60' : ''" @click="openFilePicker" @dragenter.prevent="onDragEnter" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
										<div class="flex items-center gap-4 w-full">
											<div class="h-11 w-11 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200">
												<UploadIcon />
											</div>

											<div class="min-w-0 flex-1 overflow-hidden">
												<template v-if="selectedFileName">
													<p class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate" :title="selectedFileName">
														{{ selectedFileName }}
													</p>
													<p class="text-xs text-gray-500 dark:text-gray-400">
														Click or drop to change file
													</p>
												</template>
												<template v-else>
													<p class="text-sm font-semibold text-gray-800 dark:text-gray-100">
														Drag &amp; drop your file here
													</p>
													<p class="text-xs text-gray-500 dark:text-gray-400">
														or click to browse
													</p>
												</template>
											</div>
										</div>
									</div>

									<input ref="fileInput" type="file" class="hidden" accept="*/*" @change="handleFileChange" />
								</div>
							</div>

							<div v-if="localForm.type === 'URL Link' || localForm.type === 'Online form'" class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
								<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
									{{ localForm.type === 'URL Link' ? 'URL Link' : 'Online Form' }}
								</h4>
								<div>
									<label class="label">{{ localForm.type === 'URL Link' ? 'URL Link' : 'Online Form URL' }} <span class="text-danger">*</span></label>
									<input v-model="localForm.url_link" type="text" class="input" :class="{ 'border-danger dark:border-danger-dark': linkError }" placeholder="https://..." />
									<p v-if="linkError" class="error-message">{{ linkError }}</p>
								</div>
							</div>
						</div>
					</div>

					<div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex flex-col gap-2 sm:px-6 sm:flex-row-reverse sm:gap-0">
						<button type="button"
							class="w-full sm:w-auto px-4 py-2 rounded-xl font-semibold text-sm bg-primary hover:bg-secondary text-white shadow-sm hover:shadow-md flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#ff8e48]/60 disabled:opacity-50 sm:ml-3"
							@click="handleSave" :disabled="loading">
							<Loader2 v-if="loading" class="w-4 h-4 mr-2 animate-spin" />
							<span v-if="mode === 'add'">Add</span>
							<span v-else>Update</span>
						</button>
						<button type="button"
							class="w-full sm:w-auto px-4 py-2 rounded-xl font-semibold text-sm bg-slate-50 dark:bg-slate-700/30 text-slate-700 dark:text-slate-200 border border-slate-200/60 dark:border-slate-700/60 hover:bg-slate-100 dark:hover:bg-slate-700/50 disabled:opacity-50"
							@click="$emit('close')" :disabled="loading">
							Cancel
						</button>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed, onMounted, onUnmounted } from 'vue'
import UploadIcon from '@/components/icons/UploadIcon.vue'
import { positionService, type Position } from '@/services/positionService'
import { employeeService, type Employee } from '@/services/employeeService'
import { Loader2, X } from 'lucide-vue-next'
import { useToast } from 'vue-toastification'

const toast = useToast()
const toastTimeout = 5000

const MAX_EMPLOYEE_RESULTS = 50
const INITIAL_EMPLOYEE_PREVIEW = 10

interface MaterialForm {
  title: string
  description: string
  type: 'Online form' | 'URL Link' | 'Doc File'
  source: File | string | null
  url_link: string
  status: 'active' | 'inactive'
  position_viewers: number[]
  user_viewers: number[]
}

const props = defineProps<{
  isOpen: boolean
  mode: 'add' | 'edit'
  form: MaterialForm
  error?: string | null
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', form: MaterialForm): void
}>()

const localForm = reactive({
  title: '',
  description: '',
  type: 'Doc File' as MaterialForm['type'],
  source: null as File | string | null,
  url_link: '',
  status: 'active' as MaterialForm['status'],
})

const fileInput = ref<HTMLInputElement | null>(null)
const isDragOver = ref(false)
const linkError = ref<string>('')
const localError = ref<string>('')
const positions = ref<Position[]>([])
const employees = ref<Employee[]>([])
const fetching = ref(false)

const selectedPositionIds = ref<number[]>([])
const selectedEmployeeIds = ref<number[]>([])
const positionSearch = ref('')
const employeeSearch = ref('')
const positionOpen = ref(false)
const employeeOpen = ref(false)
const positionContainer = ref<HTMLElement | null>(null)
const employeeContainer = ref<HTMLElement | null>(null)

const filteredPositions = computed(() => {
  if (!positionSearch.value) return positions.value
  const q = positionSearch.value.toLowerCase()
  return positions.value.filter(p => p.name.toLowerCase().includes(q))
})

const getEmployeeDisplayName = (e: Employee) => {
  const name = e.first_name ? `${e.first_name} ${e.last_name}` : (e.username || '')
  return e.position ? `${name} (${e.position})` : name
}

const sortedEmployees = computed(() =>
  [...employees.value].sort((a, b) =>
    getEmployeeDisplayName(a).localeCompare(getEmployeeDisplayName(b))
  )
)

const filteredEmployees = computed(() => {
  const q = employeeSearch.value.trim().toLowerCase()
  const selectedSet = new Set(selectedEmployeeIds.value)

  let pool = sortedEmployees.value
  if (q) {
    pool = pool.filter(e => getEmployeeDisplayName(e).toLowerCase().includes(q))
  }

  const selectedItems = pool.filter(e => selectedSet.has(e.id))
  const unselectedItems = pool.filter(e => !selectedSet.has(e.id))

  if (!q) {
    return [...selectedItems, ...unselectedItems.slice(0, INITIAL_EMPLOYEE_PREVIEW)]
  }

  const remaining = Math.max(0, MAX_EMPLOYEE_RESULTS - selectedItems.length)
  return [...selectedItems, ...unselectedItems.slice(0, remaining)]
})

const employeeListHint = computed(() => {
  if (!employees.value.length) return ''
  const q = employeeSearch.value.trim()
  const total = employees.value.length

  if (!q && total > INITIAL_EMPLOYEE_PREVIEW) {
    return `Showing first ${INITIAL_EMPLOYEE_PREVIEW} of ${total} — type to search all employees`
  }
  if (q && filteredEmployees.value.length >= MAX_EMPLOYEE_RESULTS) {
    return 'Too many matches — refine your search'
  }
  return ''
})

const displayError = computed(() => props.error || localError.value)

const getPositionName = (id: number) => positions.value.find(p => p.id === id)?.name || 'Unknown'

const getEmployeeName = (id: number) => {
  const emp = employees.value.find(e => e.id === id)
  if (!emp) return 'Unknown'
  return getEmployeeDisplayName(emp) || 'Unknown'
}

const removePosition = (id: number) => {
  selectedPositionIds.value = selectedPositionIds.value.filter(v => v !== id)
}

const removeEmployee = (id: number) => {
  selectedEmployeeIds.value = selectedEmployeeIds.value.filter(v => v !== id)
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (positionContainer.value && !positionContainer.value.contains(target)) {
    positionOpen.value = false
  }
  if (employeeContainer.value && !employeeContainer.value.contains(target)) {
    employeeOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})

let dependenciesFetched = false

const fetchDependencies = async () => {
  if (dependenciesFetched) return
  fetching.value = true
  try {
    const [posRes, empRes] = await Promise.all([
      positionService.getPositions(),
      employeeService.getEmployees({ per_page: 1000 })
    ])
    positions.value = posRes
    employees.value = empRes.data || []
    dependenciesFetched = true
  } catch (err) {
    console.error('Failed to fetch dependencies:', err)
  } finally {
    fetching.value = false
  }
}

const syncFormFromProps = (newForm: MaterialForm) => {
  localForm.title = newForm.title
  localForm.description = newForm.description || ''
  localForm.type = newForm.type || 'Doc File'
  localForm.source = newForm.source
  localForm.url_link = newForm.url_link || ''
  localForm.status = newForm.status || 'active'
  selectedPositionIds.value = newForm.position_viewers ? [...newForm.position_viewers] : []
  selectedEmployeeIds.value = newForm.user_viewers ? [...newForm.user_viewers] : []
}

watch(() => props.form, syncFormFromProps, { immediate: true })

watch(() => props.isOpen, (open) => {
  if (open) {
    fetchDependencies()
    syncFormFromProps(props.form)
    localError.value = ''
    positionSearch.value = ''
    employeeSearch.value = ''
    positionOpen.value = false
    employeeOpen.value = false
  } else {
    isDragOver.value = false
    if (fileInput.value) fileInput.value.value = ''
    linkError.value = ''
    localError.value = ''
    positionSearch.value = ''
    employeeSearch.value = ''
    positionOpen.value = false
    employeeOpen.value = false
  }
})

watch(() => localForm.type, (newType, oldType) => {
  if (newType !== oldType) {
    linkError.value = ''
    localError.value = ''
  }
})

const openFilePicker = () => fileInput.value?.click()
const onDragEnter = () => (isDragOver.value = true)
const onDragOver = () => (isDragOver.value = true)
const onDragLeave = () => (isDragOver.value = false)

const onDrop = (e: DragEvent) => {
  isDragOver.value = false
  const file = e.dataTransfer?.files?.[0] ?? null
  if (!file) return

  const MAX_FILE_SIZE = 25 * 1024 * 1024
  if (file.size > MAX_FILE_SIZE) {
    localError.value = 'Error: The file size exceeds 25MB.'
    toast.error(localError.value, { timeout: toastTimeout })
    return
  }

  localForm.source = file
  if (fileInput.value) fileInput.value.value = ''
}

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0] ?? null

  const MAX_FILE_SIZE = 25 * 1024 * 1024
  if (file && file.size > MAX_FILE_SIZE) {
    localError.value = 'Error: The file size exceeds 25MB.'
    toast.error(localError.value, { timeout: toastTimeout })
    if (fileInput.value) fileInput.value.value = ''
    return
  }

  localError.value = ''
  localForm.source = file
}

const selectedFileName = computed(() => {
  const src = localForm.source
  if (src instanceof File) return src.name
  if (typeof src === 'string' && src.length) return src.split('/').pop() || ''
  return ''
})

const validateForm = (): boolean => {
  localError.value = ''
  linkError.value = ''

  if (!localForm.title) {
    localError.value = 'Title is required.'
    toast.error(localError.value, { timeout: toastTimeout })
    return false
  }

  if (props.mode === 'add' && selectedPositionIds.value.length === 0 && selectedEmployeeIds.value.length === 0) {
    localError.value = 'Please select at least one viewer.'
    toast.error(localError.value, { timeout: toastTimeout })
    return false
  }

  if (localForm.type === 'URL Link' || localForm.type === 'Online form') {
    if (!localForm.url_link) {
      linkError.value = 'Please enter a URL.'
      toast.error(linkError.value, { timeout: toastTimeout })
      return false
    }
    if (!/^https?:\/\//i.test(localForm.url_link)) {
      linkError.value = 'Link must start with https:// or http://'
      toast.error(linkError.value, { timeout: toastTimeout })
      return false
    }
  } else {
    if (props.mode === 'add' && !localForm.source) {
      localError.value = 'Please select a file to upload.'
      toast.error(localError.value, { timeout: toastTimeout })
      return false
    }
  }

  return true
}

const handleSave = () => {
  if (!validateForm()) return
  emit('save', {
    ...localForm,
    position_viewers: [...selectedPositionIds.value],
    user_viewers: [...selectedEmployeeIds.value],
  })
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
